import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "./api";

export interface Me {
  accountId: string;
  userId: string;
  scopes: string[];
  role?: string;
  email?: string;
  isPlatformAdmin?: boolean;
}

export interface Account {
  id: string;
  name: string;
  slug: string;
  status: "ACTIVE" | "SUSPENDED" | "PENDING";
  planTier: string;
  monthlyQuota: number;
}

interface AuthState {
  me: Me | null;
  account: Account | null;
  loading: boolean;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      const m = await api.get<Me>("/v1/auth/me");
      setMe(m);
      try {
        const a = await api.get<Account>("/v1/accounts/me");
        setAccount(a);
      } catch {
        setAccount(null);
      }
    } catch {
      setMe(null);
      setAccount(null);
    } finally {
      setLoading(false);
    }
  }

  async function logout() {
    await api.post("/v1/auth/logout").catch(() => {});
    setMe(null);
    setAccount(null);
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <Ctx.Provider value={{ me, account, loading, logout, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth used outside AuthProvider");
  return ctx;
}
